import React, { useMemo } from 'react';
import { Search, FilterAltOff } from '@mui/icons-material';
import ReactSelect from '@components/atoms/ReactSelect';
import { Button } from '@components/atoms/Button';
import { InvoiceFilterInput } from '@models/invoice.model';
import { Project } from '@models/project.model';

interface TaskOption {
  taskId: string;
  name: string;
  projectId?: string;
}

interface Props {
  filter: InvoiceFilterInput;
  onChange: (next: InvoiceFilterInput) => void;
  projects: Project[];
  tasks: TaskOption[];
  isLoadingTasks?: boolean;
  // Compact variant for the "Pending Invoices" tab: the list is sourced from activities,
  // so only the Project filter applies. We keep the bar mounted (no layout jump) and show
  // just Project + Clear with a helper note instead of hiding it or disabling every input.
  pendingMode?: boolean;
}

export const InvoicesFilterBar: React.FC<Props> = ({
  filter,
  onChange,
  projects,
  tasks,
  isLoadingTasks,
  pendingMode = false,
}) => {
  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => p.projectId)
        .map((p) => ({ value: p.projectId as string, label: p.name })),
    [projects],
  );

  const taskOptions = useMemo(
    () => tasks.map((t) => ({ value: t.taskId, label: t.name })),
    [tasks],
  );

  const selectedProjects = projectOptions.filter((o) =>
    (filter.projectIds || []).includes(o.value),
  );
  const selectedTasks = taskOptions.filter((o) => (filter.taskIds || []).includes(o.value));

  const taskFilterDisabled = !filter.projectIds || filter.projectIds.length === 0;

  const hasAnyFilter =
    (filter.projectIds && filter.projectIds.length > 0) ||
    (filter.taskIds && filter.taskIds.length > 0) ||
    Boolean(filter.dateFrom) ||
    Boolean(filter.dateTo) ||
    Boolean(filter.search && filter.search.length > 0);

  const hasProjectFilter = Boolean(filter.projectIds && filter.projectIds.length > 0);

  // Compact Pending-tab bar: only Project is meaningful against the activity feed.
  if (pendingMode) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex flex-col gap-1 md:w-1/3">
            <label className="text-xs uppercase tracking-wide text-gray-500">Project</label>
            <ReactSelect
              isMulti
              isClearable
              placeholder="All projects"
              options={projectOptions}
              value={selectedProjects}
              onChange={(value) => {
                const ids = Array.isArray(value)
                  ? (value as Array<{ value: string }>).map((v) => v.value)
                  : [];
                onChange({
                  ...filter,
                  projectIds: ids.length > 0 ? ids : undefined,
                  taskIds: undefined,
                });
              }}
            />
          </div>
          {hasProjectFilter && (
            <Button
              variant="tertiary"
              size="md"
              leftIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
              onClick={() => onChange({ ...filter, projectIds: undefined, taskIds: undefined })}>
              Clear filters
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-400">
          Pending invoices are filtered by project.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Project filter */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">Project</label>
          <ReactSelect
            isMulti
            isClearable
            placeholder="All projects"
            options={projectOptions}
            value={selectedProjects}
            onChange={(value) => {
              const ids = Array.isArray(value)
                ? (value as Array<{ value: string }>).map((v) => v.value)
                : [];
              onChange({
                ...filter,
                projectIds: ids.length > 0 ? ids : undefined,
                // Clear task selection when project selection changes — tasks belong to projects.
                taskIds: undefined,
              });
            }}
          />
        </div>

        {/* Task filter (depends on project) */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">Task</label>
          <ReactSelect
            isMulti
            isClearable
            isDisabled={taskFilterDisabled}
            isLoading={isLoadingTasks}
            placeholder={taskFilterDisabled ? 'Select a project first' : 'All tasks'}
            options={taskOptions}
            value={selectedTasks}
            onChange={(value) => {
              const ids = Array.isArray(value)
                ? (value as Array<{ value: string }>).map((v) => v.value)
                : [];
              onChange({ ...filter, taskIds: ids.length > 0 ? ids : undefined });
            }}
          />
        </div>

        {/* Search */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">Search</label>
          <div className="relative">
            <Search
              sx={{ fontSize: 18 }}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={filter.search || ''}
              onChange={(e) => onChange({ ...filter, search: e.target.value || undefined })}
              placeholder="Invoice # or email"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">From</label>
          <input
            type="date"
            value={filter.dateFrom || ''}
            onChange={(e) => onChange({ ...filter, dateFrom: e.target.value || undefined })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-gray-500">To</label>
          <input
            type="date"
            value={filter.dateTo || ''}
            onChange={(e) => onChange({ ...filter, dateTo: e.target.value || undefined })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {hasAnyFilter && (
          <Button
            variant="tertiary"
            size="md"
            leftIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
            onClick={() => onChange({})}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
};
